import React from 'react';
import {
  PurchaseOrderStatus,
  PurchaseOrderStatusHistory,
} from '@/types/purchase-order';

interface PurchaseOrderStatusTimelineProps {
  statusHistories: PurchaseOrderStatusHistory[];
}

const formatTimelineDateTime = (value: string) => {
  if (!value) {
    return { date: '-', time: '' };
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return { date: value, time: '' };
  }

  const date = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(parsed);

  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);

  return { date, time };
};

const getStatusPillClasses = (status?: string) => {
  switch (status) {
    case PurchaseOrderStatus.CREATED:
      return 'bg-yellow-100 text-yellow-800';
    case PurchaseOrderStatus.PENDING_REVIEW:
      return 'bg-indigo-100 text-indigo-800';
    case PurchaseOrderStatus.APPROVED:
      return 'bg-green-100 text-[#1FC16B]';
    case PurchaseOrderStatus.REJECTED:
      return 'bg-[#FB3748]/10 text-[#FB3748]';
    case PurchaseOrderStatus.CLOSED:
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const formatStatusLabel = (status?: string) => {
  if (!status) return 'UNKNOWN';
  return status.replace(/_/g, ' ');
};

const MetaRow: React.FC<{
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, children }) => (
  <div className="flex items-start gap-2">
    <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
      {icon}
    </span>
    <div className="flex-1 min-w-0 mt-1">{children}</div>
  </div>
);

const UserIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={`h-3 w-3 ${className}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

const PurchaseOrderStatusTimeline: React.FC<
  PurchaseOrderStatusTimelineProps
> = ({ statusHistories }) => {
  return (
    <div className="w-full max-w-full overflow-hidden sticky top-5 rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="px-5 py-4">
        <div className="text-base font-semibold text-gray-900">
          Status Timeline
        </div>
      </div>

      <div className="border-t border-gray-100 bg-gray-50 px-5 py-2">
        <div className="text-xs font-medium tracking-wide text-gray-400">
          Purchase Order Progress
        </div>
      </div>

      <div className="px-5 py-4">
        <div className="space-y-4">
          {statusHistories.length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400">
              No status history available yet.
            </div>
          )}

          {statusHistories.map((entry, index) => {
            const { date, time } = formatTimelineDateTime(entry.createdAt);
            const isCurrent = index === 0;
            const detailTextClass = isCurrent ? 'text-black' : 'text-gray-500';
            const iconClass = isCurrent ? 'text-black' : 'text-gray-400';
            const timeTextClass = isCurrent ? 'text-black' : 'text-gray-500';
            const statusPill = getStatusPillClasses(entry.toStatus);
            const statusLabel = formatStatusLabel(entry.toStatus);
            const metadata = entry.metadata;
            const rejectionReason = metadata?.rejectionReason;

            // Receive batches record { totalItems, items: [{ platNomor, ... }] }
            const receivedItems: any[] = Array.isArray(metadata?.items)
              ? (metadata?.items as any[])
              : [];
            const totalReceiveUnits = metadata?.totalItems ?? metadata?.totalReceiveUnits;
            const platNomor = receivedItems
              .map((item) => item?.platNomor)
              .filter(Boolean)
              .join(', ');

            return (
              <div key={entry.ID} className="flex items-start gap-3">
                {/* ── Timeline bullet & vertical connector ── */}
                <div className="flex flex-col items-center self-stretch">
                  <div
                    className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                      isCurrent
                        ? 'border-purple-400 text-purple-600'
                        : 'border-gray-300 text-gray-400'
                    }`}
                  >
                    {isCurrent ? (
                      <div className="h-2.5 w-2.5 rounded-full bg-purple-600" />
                    ) : (
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                  {index !== statusHistories.length - 1 && (
                    <div className="w-px flex-1 bg-gray-200" />
                  )}
                </div>

                {/* ── Entry body ── */}
                <div className="min-w-0 flex-1 pb-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900">
                        {date}
                      </div>
                      <span
                        className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusPill}`}
                      >
                        {statusLabel}
                      </span>
                    </div>

                    <div className={`text-xs flex-shrink-0 ${timeTextClass}`}>
                      {time}
                    </div>
                  </div>

                  <div className={`mt-2 space-y-1.5 text-xs ${detailTextClass}`}>
                    <MetaRow icon={<UserIcon className={iconClass} />}>
                      <span className="break-all">{entry.createdBy}</span>
                    </MetaRow>

                    {totalReceiveUnits !== undefined &&
                      totalReceiveUnits !== null && (
                        <MetaRow
                          icon={
                            <i
                              className={`ri-stack-line text-xs ${iconClass}`}
                            />
                          }
                        >
                          <div className="text-gray-400">
                            Total Receive Units:
                          </div>
                          <div className={`mt-0.5 ${detailTextClass}`}>
                            {totalReceiveUnits}
                          </div>
                        </MetaRow>
                      )}

                    {platNomor && (
                      <MetaRow
                        icon={
                          <i className={`ri-car-line text-xs ${iconClass}`} />
                        }
                      >
                        <div className="text-gray-400">Plat Nomor:</div>
                        <div
                          className={`mt-0.5 break-words ${detailTextClass}`}
                        >
                          {platNomor}
                        </div>
                      </MetaRow>
                    )}

                    {rejectionReason && (
                      <MetaRow
                        icon={
                          <i
                            className={`ri-error-warning-line text-xs ${iconClass}`}
                          />
                        }
                      >
                        <div className="text-gray-400">Rejection Reason:</div>
                        <div
                          className={`mt-0.5 break-words whitespace-pre-line ${detailTextClass}`}
                        >
                          {rejectionReason}
                        </div>
                      </MetaRow>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderStatusTimeline;
